import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(
    typeof window !== "undefined" && /(?:mode=)(signup|register)/.test(window.location.search) ? "register" : "login"
  );
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.target_path ? "/dashboard" : "/onboarding");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success("Welcome back!");
        navigate(u.role === "admin" ? "/admin" : u.target_path ? "/dashboard" : "/onboarding");
      } else {
        const u = await register(form);
        toast.success("Account created!");
        navigate("/onboarding");
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex noise-overlay">
      <div className="hidden lg:flex flex-1 bg-card border-r border-border relative overflow-hidden flex-col justify-center px-16">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-primary/15 rounded-full blur-[100px]" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-2xl tracking-tight">Elite Hire India</span>
          </div>
          <h2 className="font-heading text-4xl font-black tracking-tighter leading-none mb-4">
            Train like a<br />placement pro.
          </h2>
          <p className="text-muted-foreground max-w-md leading-relaxed">
            Join thousands of freshers preparing for AMCAT, Versant and company placement tests with AI-powered practice.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-black tracking-tight mb-2">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {mode === "login" ? "Continue your preparation." : "Start your placement journey today."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-md" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-md" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-md" placeholder="••••••••" />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <Button type="submit" className="w-full rounded-md h-11" disabled={loading} data-testid="auth-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-6">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              className="text-primary font-medium hover:underline"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              data-testid="auth-mode-toggle"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
